import { ENDPIONTS, httpService } from "@api";
import { PayrollActions } from "@viewModels";
import Swal from "sweetalert2";

interface PayrollRef {
  id: number;
  title: string; // e.g. "Waaxda Dhulka - 2026-09"
  actions: PayrollActions;
}

// onDone runs as soon as the request succeeds: the API's success popup replaces this dialog, so its result
// can't be relied on.
type OnDone = () => void;

// Confirms and moves the payroll to the next approval stage.
export const confirmAdvancePayroll = async ({ id, title, actions }: PayrollRef, onDone: OnDone) => {
  const label = actions.advanceLabel ?? "Approve";
  await Swal.fire({
    title: `${label}?`,
    html: `<p>Payroll <strong>${escapeHtml(title)}</strong></p><p class="text-muted mb-0">Currently waiting for: ${escapeHtml(actions.waitingFor)}</p>`,
    input: "textarea",
    inputLabel: "Remarks (optional)",
    inputPlaceholder: "Add a note for the next approver…",
    icon: "question",
    showCancelButton: true,
    confirmButtonText: label,
    allowOutsideClick: false,
    showLoaderOnConfirm: true,
    preConfirm: async (remarks: string) => {
      const res = await httpService(ENDPIONTS.advancePayroll).update(id, { remarks } as any);
      if (res?.status === 204) onDone();
    },
  });
};

// Asks for the reason (and the target when the user may also return to Finance) and returns the payroll.
export const confirmReturnPayroll = async ({ id, title, actions }: PayrollRef, onDone: OnDone) => {
  let target = "HR";
  if (actions.canReturnToFinance) {
    const choice = await Swal.fire({
      title: "Return payroll to…",
      input: "radio",
      inputOptions: { HR: "HR (to correct the payroll)", Finance: "Head of Finance (to review again)" },
      inputValue: "HR",
      showCancelButton: true,
      confirmButtonText: "Next",
    });
    if (!choice.isConfirmed) return;
    target = choice.value;
  }

  await Swal.fire({
    title: `Return to ${target === "Finance" ? "Head of Finance" : "HR"}`,
    html: `<p>Payroll <strong>${escapeHtml(title)}</strong></p>`,
    input: "textarea",
    inputLabel: "What needs to be corrected?",
    inputPlaceholder: "Explain the problem so it can be fixed…",
    inputValidator: (value: string) => (!value?.trim() ? "Please explain the reason for returning it." : null),
    icon: "warning",
    showCancelButton: true,
    confirmButtonText: "Return payroll",
    confirmButtonColor: "#dc2626",
    allowOutsideClick: false,
    showLoaderOnConfirm: true,
    preConfirm: async (remarks: string) => {
      const res = await httpService(ENDPIONTS.returnPayroll).update(id, { target, remarks } as any);
      if (res?.status === 204) onDone();
    },
  });
};

const escapeHtml = (value: string) =>
  (value ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c] as string));
